// Components guide — the category rail for inserting elements onto the canvas.
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const componentsGuide = {
  id: "components",
  icon: "widgets",
  keywords: [
    "components",
    "bileşenler",
    "elements",
    "öğeler",
    "text",
    "metin",
    "rich text",
    "zengin metin",
    "image",
    "görsel",
    "resim",
    "shape",
    "şekil",
    "table",
    "tablo",
    "chart",
    "graphic",
    "grafik",
    "icon",
    "simge",
    "ikon",
    "animation",
    "animasyon",
    "character",
    "karakter",
    "youtube",
    "click menu",
    "tıklama menüsü",
    "smart elements",
    "akıllı öğeler",
    "sounds",
    "sesler",
    "music",
    "müzik",
    "sound effect",
    "ses efekti",
    "jingle",
    "cıngıl",
    "my effects",
    "efektlerim",
    "click sound",
    "tıklama sesi",
    "scene sound",
    "sahne sesi",
    "insert",
    "ekle",
    "componentes",
    "elementos",
    "texto enriquecido",
    "imagen",
    "forma",
    "tabla",
    "gráfico",
    "iconos",
    "animaciones",
    "personajes",
    "sonidos",
    "música",
    "efectos de sonido",
    "elementos inteligentes",
    "insertar",
  ],
  content: {
    en: {
      feature: "Components",
      title: "Inserting components",
      sections: [
        {
          id: "overview",
          title: "Opening the components panel",
          videoUrl: null,
          text:
            "Click the **Components** button (Beta) in the top bar of the editor to open the category rail on the left.\n\n" +
            "- The rail lists every category as an icon + label: **Elements**, **Smart Elements** (Beta), **Click Menu**, **Interactive**, **Icons**, **Animations**, **Characters**, **Youtube**, and **Sounds** (Beta).\n" +
            "- Click a category to open its panel, then either **click** an item to add it to the current scene, or **drag** it onto the canvas to drop it exactly where you want. The panel stays open, so you can add several items in a row.\n" +
            "- Close a panel with the × in its corner, or with the arrow tab on its edge.\n\n" +
            "In a CLM presentation the rail only offers **Elements**, **Smart Elements**, **Click Menu**, and **Icons**. While an interactive scenario element is selected on the canvas, the rail is dimmed and its panels stay closed.",
        },
        {
          id: "basic-elements",
          title: "Elements: text, images, shapes and more",
          videoUrl: null,
          text:
            "The **Elements** category holds the core building blocks every scene is made of.\n\n" +
            "- **Rich Text**, **Image**, **Video Player**, **PDF Viewer**, and **Shape Overlay**, which you can switch between rectangle, arrow, circle, highlight, and blur, in any color.\n" +
            "- A **Data** group adds **Table** and **Graphic** (chart) elements for structured content.\n" +
            "- An **Other** group adds **Audio Cue** and **HTML Content** (both Beta). HTML Content can only be clicked, not dragged: it opens a dialog where you paste custom HTML or tick **Screen Training** to use the ready-made screen-training template.",
        },
        {
          id: "media-decor",
          title: "Icons, animations, characters & YouTube",
          videoUrl: null,
          text:
            "**Icons**, **Animations**, and **Characters** are grids of ready-made graphics. Click one or drag it onto the canvas, and it's inserted as an image.\n\n" +
            "- **Icons**: icons, arrows, speech balloons, and basic shapes.\n" +
            "- **Animations**: animated graphics, such as moving arrows.\n" +
            "- **Characters**: illustrated people.\n" +
            "- Each of these panels has a search box, and the eye button on a tile opens a larger preview.\n" +
            "- **Youtube**: type a topic in the **Search on YouTube** box and press Enter. Preview a result, then click or drag it in to add it as a video player.\n\n" +
            "If you choose **Replace File** on an icon, animation, character, or YouTube video, the matching category opens, and the item you pick replaces the old one in place.",
        },
        {
          id: "interactions",
          title: "Click Menu & Interactive elements",
          videoUrl: null,
          text:
            "**Click Menu** adds clickable hotspot elements: **Click**, plus two Beta elements used by imported screen-training guides, **Screen Training Multi-Input** and **Screen Training Tag Display**.\n\n" +
            "- The **Interactive** category holds the quiz and assessment elements and the interactive scenario element, with a search box at the top. See the Assessments guide for the full walkthrough.",
        },
        {
          id: "smart-elements",
          title: "Smart Elements (Beta)",
          videoUrl: null,
          text:
            "**Smart Elements** (Beta) are ready-made composite elements. They're the same styled building blocks used in AI-built scenes, and each one combines text and layout instead of being a single primitive.\n\n" +
            "- Grouped by **Text** (e.g. Heading, Quote), **Lists** (e.g. Bullet List, Card Grid, Timeline, Stats), **Layout** (e.g. Hero, Two Column, Comparison), and **UI** (e.g. Badge, CTA Button, Icon Block).\n" +
            "- Click or drag one in like any other element. It arrives with sample content that you replace with your own.",
        },
        {
          id: "sounds-library",
          title: "Sounds (Beta): music and sound effects",
          videoUrl: null,
          text:
            "**Sounds** (Beta) is a ready-made audio library.\n\n" +
            "- Use the chips at the top to narrow it down: **All**, **Sound Effects**, **Music**, **Jingles**, or **My Effects**. Sounds are grouped into folders, and inside a folder you can also filter by length (**Short**, **Medium**, **Long**). The search box searches across all of them.\n" +
            "- Play a sound and click its progress bar to jump to any point.\n" +
            "- **Add as click sound** puts it on the canvas as an Audio Cue element. **Add as scene sound** adds it to the scene's sound track instead.",
        },
        {
          id: "sound-effects-ai",
          title: "Creating sound effects with AI",
          videoUrl: null,
          text:
            "Under **Sounds**, open the **My Effects** chip to generate your own sound effects with AI. They're saved for your organization.\n\n" +
            "- Open **Create Effect** and describe the sound you want.\n" +
            "- Optional settings: **Enrich with AI** (refines your description), **Loop**, **Duration (s)** (auto or a fixed length), **Prompt influence** (Low, Medium, or High), and **Count** (how many variations to make).\n" +
            "- Click **Generate Effect**. New effects appear in the list, where you can play them, add them as a click sound or scene sound, or delete them.",
        },
      ],
    },
    tr: {
      feature: "Bileşenler",
      title: "Bileşen ekleme",
      sections: [
        {
          id: "overview",
          title: "Bileşenler panelini açma",
          videoUrl: null,
          text:
            "Soldaki kategori rafını açmak için editörün üst çubuğundaki **Bileşenler** (Beta) düğmesine tıklayın.\n\n" +
            "- Rafta her kategori simge ve etiketiyle listelenir: **Öğeler**, **Akıllı Öğeler** (Beta), **Tıklama Menüsü**, **Etkileşim**, **İkonlar**, **Animasyonlar**, **Karakterler**, **Youtube** ve **Sesler** (Beta).\n" +
            "- Bir kategoriye tıklayıp panelini açın. Ardından bir öğeyi geçerli sahneye eklemek için **tıklayın** ya da tam istediğiniz yere bırakmak için tuvale **sürükleyin**. Panel açık kaldığından art arda birden fazla öğe ekleyebilirsiniz.\n" +
            "- Paneli köşesindeki × ile ya da kenarındaki ok sekmesiyle kapatın.\n\n" +
            "CLM sunumlarında rafta yalnızca **Öğeler**, **Akıllı Öğeler**, **Tıklama Menüsü** ve **İkonlar** bulunur. Tuvalde etkileşimli senaryo öğesi seçiliyken raf soluklaşır ve panelleri açılmaz.",
        },
        {
          id: "basic-elements",
          title: "Öğeler: metin, görsel, şekil ve daha fazlası",
          videoUrl: null,
          text:
            "**Öğeler** kategorisi, her sahnenin temel yapı taşlarını içerir.\n\n" +
            "- **Zengin Metin**, **Resim**, **Video**, **PDF Gösterici** ve istediğiniz renkte dikdörtgen, ok, daire, vurgu ya da bulanıklaştırma olarak ayarlanabilen **Şekil Kaplaması**.\n" +
            "- **Veri** grubu, yapılandırılmış içerik için **Tablo** ve **Grafik** öğelerini ekler.\n" +
            "- **Diğer** grubu **Ses İşareti** ve **HTML İçerik** (ikisi de Beta) öğelerini ekler. HTML İçerik sürüklenmez, yalnızca tıklanarak eklenir. Açılan pencereye özel HTML yapıştırabilir ya da hazır ekran eğitimi şablonunu kullanmak için **Ekran Eğitimi** kutusunu işaretleyebilirsiniz.",
        },
        {
          id: "media-decor",
          title: "İkonlar, animasyonlar, karakterler ve YouTube",
          videoUrl: null,
          text:
            "**İkonlar**, **Animasyonlar** ve **Karakterler**, hazır görsellerden oluşan ızgaralardır. Birine tıkladığınızda ya da tuvale sürüklediğinizde resim olarak eklenir.\n\n" +
            "- **İkonlar**: simgeler, oklar, konuşma balonları ve temel şekiller.\n" +
            "- **Animasyonlar**: hareketli oklar gibi animasyonlu görseller.\n" +
            "- **Karakterler**: çizim insan figürleri.\n" +
            "- Bu panellerin her birinde bir arama kutusu vardır. Bir kutucuktaki göz düğmesi büyük ön izlemeyi açar.\n" +
            "- **Youtube**: **YouTube'da ara** kutusuna bir konu yazıp Enter'a basın. Sonuçları ön izleyin, ardından birini tıklayarak ya da sürükleyerek video oynatıcı olarak ekleyin.\n\n" +
            "Bir ikon, animasyon, karakter ya da YouTube videosunda **Dosyayı Değiştir**'i seçerseniz ilgili kategori açılır. Seçtiğiniz öğe eskisinin yerine geçer.",
        },
        {
          id: "interactions",
          title: "Tıklama Menüsü ve Etkileşim öğeleri",
          videoUrl: null,
          text:
            "**Tıklama Menüsü**, tıklanabilir etkin alan öğeleri ekler: **Tıklama** ve içe aktarılan ekran eğitimi kılavuzlarının kullandığı iki Beta öğe, **Ekran Eğitimi Çoklu Giriş** ile **Ekran Eğitimi Etiket Gösterimi**.\n\n" +
            "- **Etkileşim** kategorisinde soru ve değerlendirme öğeleriyle etkileşimli senaryo öğesi bulunur. Üstte bir arama kutusu vardır. Ayrıntılı anlatım için Değerlendirmeler rehberine bakın.",
        },
        {
          id: "smart-elements",
          title: "Akıllı Öğeler (Beta)",
          videoUrl: null,
          text:
            "**Akıllı Öğeler** (Beta), hazır birleşik öğelerdir. Yapay zekâyla oluşturulan sahnelerde kullanılan stilli yapı taşlarının aynısıdır ve tek bir temel öğe yerine metin ile yerleşimi bir araya getirir.\n\n" +
            "- **Metin** (ör. Başlık, Alıntı), **Listeler** (ör. Maddeleme, Kartlar, Zaman Çizelgesi, İstatistikler), **Düzen** (ör. Ana başlık, İkili Sütun, Karşılaştırma) ve **UI Öğeleri** (ör. Etiket, Aksiyon Butonu, İkon Bloğu) olarak gruplanır.\n" +
            "- Diğer öğeler gibi tıklayarak ya da sürükleyerek ekleyin. Öğe örnek içerikle gelir, bu içeriği kendinizinkiyle değiştirirsiniz.",
        },
        {
          id: "sounds-library",
          title: "Sesler (Beta): müzik ve ses efektleri",
          videoUrl: null,
          text:
            "**Sesler** (Beta), hazır bir ses kütüphanesidir.\n\n" +
            "- Üstteki seçeneklerle daraltın: **Tümü**, **Ses Efektleri**, **Müzik**, **Cıngıllar** veya **Efektlerim**. Sesler klasörlere ayrılmıştır. Bir klasörün içinde süreye göre de (**Kısa**, **Orta**, **Uzun**) filtreleyebilirsiniz. Arama kutusu hepsinde birden arar.\n" +
            "- Bir sesi oynatın, istediğiniz noktaya atlamak için ilerleme çubuğuna tıklayın.\n" +
            "- **Tıklama sesi olarak ekle**, sesi tuvale Ses İşareti öğesi olarak koyar. **Sahne sesi olarak ekle** ise sahnenin ses kanalına ekler.",
        },
        {
          id: "sound-effects-ai",
          title: "Yapay zekâ ile ses efekti oluşturma",
          videoUrl: null,
          text:
            "**Sesler** panelinde **Efektlerim** seçeneğini açarak yapay zekâyla kendi ses efektlerinizi üretin. Efektler kurumunuz için kaydedilir.\n\n" +
            "- **Efekt Oluştur** bölümünü açıp istediğiniz sesi tarif edin.\n" +
            "- İsteğe bağlı ayarlar: **AI ile zenginleştir** (açıklamanızı iyileştirir), **Döngü**, **Süre (sn)** (otomatik ya da sabit bir uzunluk), **Prompt etkisi** (Düşük, Orta veya Yüksek) ve **Adet** (kaç varyasyon üretileceği).\n" +
            "- **Efekt Oluştur** düğmesine tıklayın. Yeni efektler listede görünür. Buradan onları oynatabilir, tıklama ya da sahne sesi olarak ekleyebilir veya silebilirsiniz.",
        },
      ],
    },
    es: {
      feature: "Componentes",
      title: "Insertar componentes",
      sections: [
        {
          id: "overview",
          title: "Abrir el panel de componentes",
          videoUrl: null,
          text:
            "Haz clic en el botón **Componentes** (Beta) de la barra superior del editor para abrir la barra de categorías a la izquierda.\n\n" +
            "- La barra muestra cada categoría con su icono y su nombre: **Elementos**, **Elementos Inteligentes** (Beta), **Menú de Clic**, **Interactivo**, **Iconos**, **Animaciones**, **Personajes**, **Youtube** y **Sonidos** (Beta).\n" +
            "- Haz clic en una categoría para abrir su panel. Después, **haz clic** en un elemento para añadirlo a la escena actual o **arrástralo** al lienzo para colocarlo justo donde quieras. El panel permanece abierto, así que puedes añadir varios elementos seguidos.\n" +
            "- Cierra un panel con la × de su esquina o con la pestaña de flecha de su borde.\n\n" +
            "En una presentación CLM, la barra solo ofrece **Elementos**, **Elementos Inteligentes**, **Menú de Clic** e **Iconos**. Mientras hay un elemento de escenario interactivo seleccionado en el lienzo, la barra se atenúa y sus paneles no se abren.",
        },
        {
          id: "basic-elements",
          title: "Elementos: texto, imágenes, formas y más",
          videoUrl: null,
          text:
            "La categoría **Elementos** contiene los bloques básicos con los que se construye cada escena.\n\n" +
            "- **Texto Enriquecido**, **Imagen**, **Reproductor Vídeo**, **Visor PDF** y **Forma Superpuesta**, que puedes cambiar entre rectángulo, flecha, círculo, resaltado y desenfoque, en cualquier color.\n" +
            "- El grupo **Datos** añade los elementos **Tabla** y **Gráfico** para contenido estructurado.\n" +
            "- El grupo **Otros** añade **Señal de audio** y **Contenido HTML** (ambos en Beta). Contenido HTML solo se añade con un clic, no arrastrándolo: abre un cuadro de diálogo donde puedes pegar tu propio HTML o marcar **Formación en pantalla** para usar la plantilla de formación en pantalla ya preparada.",
        },
        {
          id: "media-decor",
          title: "Iconos, animaciones, personajes y YouTube",
          videoUrl: null,
          text:
            "**Iconos**, **Animaciones** y **Personajes** son cuadrículas de gráficos ya preparados. Haz clic en uno o arrástralo al lienzo y se insertará como imagen.\n\n" +
            "- **Iconos**: iconos, flechas, bocadillos de diálogo y formas básicas.\n" +
            "- **Animaciones**: gráficos animados, como flechas en movimiento.\n" +
            "- **Personajes**: personas ilustradas.\n" +
            "- Cada uno de estos paneles tiene un cuadro de búsqueda, y el botón del ojo de cada miniatura abre una vista previa más grande.\n" +
            "- **Youtube**: escribe un tema en el cuadro **Buscar en YouTube** y pulsa Intro. Previsualiza un resultado y luego haz clic en él o arrástralo para añadirlo como reproductor de vídeo.\n\n" +
            "Si eliges **Reemplazar archivo** en un icono, una animación, un personaje o un vídeo de YouTube, se abre la categoría correspondiente y el elemento que elijas sustituye al anterior en el mismo lugar.",
        },
        {
          id: "interactions",
          title: "Menú de Clic y elementos interactivos",
          videoUrl: null,
          text:
            "**Menú de Clic** añade elementos de zona activa en los que se puede hacer clic: **Clic**, además de dos elementos Beta que usan las guías de formación en pantalla importadas, **Multi-Entrada de Formación** y **Mostrar Etiqueta de Formación**.\n\n" +
            "- La categoría **Interactivo** contiene los elementos de preguntas y evaluación y el elemento de escenario interactivo, con un cuadro de búsqueda en la parte superior. Consulta la guía de Evaluaciones para ver el recorrido completo.",
        },
        {
          id: "smart-elements",
          title: "Elementos Inteligentes (Beta)",
          videoUrl: null,
          text:
            "Los **Elementos Inteligentes** (Beta) son elementos compuestos ya preparados. Son los mismos bloques con estilo que se usan en las escenas creadas con IA, y cada uno combina texto y maquetación en lugar de ser un elemento simple.\n\n" +
            "- Se agrupan en **Texto** (p. ej., Encabezado, Cita), **Listas** (p. ej., Lista con viñetas, Cuadrícula de tarjetas, Línea de tiempo, Estadísticas), **Diseño** (p. ej., Destacado, Dos columnas, Comparación) e **Interfaz** (p. ej., Insignia, Botón CTA, Bloque de icono).\n" +
            "- Haz clic en uno o arrástralo como cualquier otro elemento. Llega con contenido de ejemplo que sustituyes por el tuyo.",
        },
        {
          id: "sounds-library",
          title: "Sonidos (Beta): música y efectos de sonido",
          videoUrl: null,
          text:
            "**Sonidos** (Beta) es una biblioteca de audio lista para usar.\n\n" +
            "- Usa los filtros de la parte superior para acotar la búsqueda: **Todos**, **Efectos de sonido**, **Música**, **Jingles** o **Mis efectos**. Los sonidos están agrupados en carpetas y, dentro de una carpeta, también puedes filtrar por duración (**Corto**, **Medio**, **Largo**). El cuadro de búsqueda busca en todos ellos.\n" +
            "- Reproduce un sonido y haz clic en su barra de progreso para saltar a cualquier punto.\n" +
            "- **Añadir como sonido de clic** lo coloca en el lienzo como un elemento Señal de audio. **Añadir como sonido de escena** lo añade a la pista de sonido de la escena.",
        },
        {
          id: "sound-effects-ai",
          title: "Crear efectos de sonido con IA",
          videoUrl: null,
          text:
            "En **Sonidos**, abre el filtro **Mis efectos** para generar tus propios efectos de sonido con IA. Se guardan para toda tu organización.\n\n" +
            "- Abre **Crear efecto** y describe el sonido que quieres.\n" +
            "- Ajustes opcionales: **Enriquecer con IA** (mejora tu descripción), **Bucle**, **Duración (s)** (automática o una duración fija), **Influencia del prompt** (Bajo, Medio o Alto) y **Cantidad** (cuántas variaciones crear).\n" +
            "- Haz clic en **Generar efecto**. Los nuevos efectos aparecen en la lista, donde puedes reproducirlos, añadirlos como sonido de clic o de escena, o eliminarlos.",
        },
      ],
    },
  },
};

export default componentsGuide;
